import React from 'react';
import type { WorkflowResponse } from '@/modules/workflows/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import {
  getSkillMeta,
  formatSkillDate,
  SkillStatusBadge,
  SkillBaseChip,
  SkillModeChip,
  SkillNodesChip,
} from './skillShared';

/**
 * Card de skill (view "Cards"). Sem checkbox de seleção (F34 — seleção só na
 * view Lista): botão de status rápido + menu ••• de ações.
 */
export interface SkillCardProps {
  skill: WorkflowResponse;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onExport: (id: string) => void;
  onDelete: (id: string) => void;
  onActivate: (id: string) => void;
  onPause: (id: string) => void;
}

export const SkillCard: React.FC<SkillCardProps> = ({
  skill,
  onEdit,
  onDuplicate,
  onExport,
  onDelete,
  onActivate,
  onPause,
}) => {
  const { description, executionMode, nodeCount, baseLabel } = getSkillMeta(skill);
  const isActive = skill.status === 'active';

  // Drag pro FolderList (mesmo contrato do WorkflowCard — 'workflow-id')
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('workflow-id', skill.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => onEdit(skill.id) },
    { label: 'Duplicar', icon: 'copy', onClick: () => onDuplicate(skill.id) },
    { label: 'Exportar', icon: 'download', onClick: () => onExport(skill.id) },
    {
      label: isActive ? 'Desativar' : 'Ativar',
      icon: isActive ? 'pause' : 'play',
      onClick: () => (isActive ? onPause(skill.id) : onActivate(skill.id)),
    },
    { label: 'Excluir', icon: 'trash', onClick: () => onDelete(skill.id), variant: 'danger' },
  ];

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors cursor-grab active:cursor-grabbing"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      draggable
      onDragStart={handleDragStart}
      data-testid={`skill-card-${skill.id}`}
    >
      {/* Header (mockup: SEM tile — título direto; F34: card sem checkbox) */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => onEdit(skill.id)}
          className="flex-1 min-w-0 text-left"
        >
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {skill.name}
          </h3>
          {description && (
            <p
              className="text-xs mt-0.5 line-clamp-2"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              {description}
            </p>
          )}
        </button>

        <div className="flex-shrink-0 flex items-center gap-0.5 -mr-1 -mt-1">
          {/* Toggle rápido de status (ativar/pausar) */}
          {(isActive || skill.status === 'inactive') && (
            <button
              type="button"
              onClick={() => (isActive ? onPause(skill.id) : onActivate(skill.id))}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors"
              style={
                isActive
                  ? { color: 'var(--theme-warning-color)', backgroundColor: 'var(--theme-warning-background)' }
                  : { color: 'var(--theme-success-color)', backgroundColor: 'var(--theme-success-background)' }
              }
              title={isActive ? 'Pausar' : 'Ativar'}
              aria-label={isActive ? 'Pausar' : 'Ativar'}
              data-testid={`skill-toggle-${skill.id}`}
            >
              <Icon name={isActive ? 'pause' : 'play'} className="w-3.5 h-3.5" />
            </button>
          )}
          <CardActionsMenu actions={actions} size="sm" data-testid={`skill-actions-${skill.id}`} />
        </div>
      </div>

      {/* Badges: base + status + modo + etapas */}
      <div className="mt-3 flex items-center flex-wrap gap-x-3 gap-y-2">
        <SkillBaseChip label={baseLabel} />
        <SkillStatusBadge status={skill.status} />
        <SkillModeChip mode={executionMode} />
        <SkillNodesChip count={nodeCount} />
      </div>

      {/* Footer: atualizado + abrir editor */}
      <div
        className="mt-3 pt-3 flex items-center justify-between text-xs"
        style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
      >
        <span className="inline-flex items-center gap-1">
          <Icon name="clock" className="w-3 h-3" />
          Atualizado {formatSkillDate(skill.updatedAt)}
        </span>
        <button
          type="button"
          onClick={() => onEdit(skill.id)}
          className="font-semibold hover:underline"
          style={{ color: 'var(--theme-primary)' }}
          data-testid={`skill-open-${skill.id}`}
        >
          Abrir editor
        </button>
      </div>
    </div>
  );
};

SkillCard.displayName = 'SkillCard';
